import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../services/api';
import androidBridge from '../services/androidBridge';

const NFCPaymentFlow: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState<'checking' | 'launching' | 'waiting' | 'completed' | 'failed' | 'unsupported'>('checking');
  const [message, setMessage] = useState<string>('Checking device capabilities...');
  const [elapsedTime, setElapsedTime] = useState(0);
  const [retryCount, setRetryCount] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const paymentInitiatedRef = useRef<boolean>(false);

  const transactionId = location.state?.transactionId;
  const amount = location.state?.amount || 0;
  const ticketType = location.state?.ticketType;
  const sessionToken = location.state?.sessionToken;

  const clearTimers = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!transactionId) {
      console.error('[NFCPayment] Missing transaction ID');
      setStatus('failed');
      setMessage('Missing transaction information. Please try again.');
      return;
    }

    const initializePayment = async () => {
      if (paymentInitiatedRef.current) return;

      // Check if running in Android app
      if (!androidBridge.isAndroidApp()) {
        console.log('[NFCPayment] Not running in Android app');
        setStatus('unsupported');
        setMessage('NFC payment is only available on Android kiosk devices. Please contact staff for assistance.');
        return;
      }

      // Check if Viva app is installed
      const capabilities = androidBridge.getPaymentCapabilities();
      console.log('[NFCPayment] Capabilities:', capabilities);

      if (!capabilities.supportsVivaIntent) {
        console.log('[NFCPayment] Viva app not installed');
        setStatus('unsupported');
        setMessage('Viva Wallet app is not installed on this device. Please contact staff for assistance.');
        return;
      }

      // Start payment
      paymentInitiatedRef.current = true;
      setStatus('launching');
      setMessage('Launching Viva Wallet app...');

      try {
        const sessionId = sessionToken || `ANDROID-${transactionId}-${Date.now()}`;
        const amountInCents = Math.round(amount * 100);

        console.log('[NFCPayment] Starting Android Intent payment:', {
          amountInCents,
          sessionId: sessionId.substring(0, 10) + '...',
          transactionId,
          usingBackendToken: !!sessionToken
        });

        androidBridge.log(`Starting NFC payment: €${amount} (${amountInCents} cents)`);

        // Start elapsed time timer
        timerIntervalRef.current = setInterval(() => {
          setElapsedTime((prev) => prev + 1);
        }, 1000);

        setStatus('waiting');
        setMessage('Tap your card or phone to pay');

        // Call Android Intent payment
        const result = await androidBridge.startVivaPayment(
          amountInCents,
          sessionId,
          transactionId
        );

        console.log('[NFCPayment] Payment result:', result);

        // Handle payment result
        if (result.status === 'success' || result.status === 'APPROVED' || result.status === 'COMPLETED') {
          setStatus('completed');
          setMessage('Payment successful! Loading your ticket...');
          clearTimers();

          try {
            // Update backend with payment success
            console.log('[NFCPayment] Updating backend with payment result');
            await api.post(`/payment/viva/pos-payment-complete`, {
              transaction_id: transactionId,
              status: 'success',
              viva_transaction_id: result.transactionId,
              session_id: result.sessionId,
              payment_method: 'CARD_PRESENT',
            });

            // Fetch ticket
            console.log('[NFCPayment] Fetching ticket');
            const ticketResponse = await api.get(`/transaction/${transactionId}/ticket`);

            // Navigate to receipt page with ticket - NFC flow shows ticket QR for user to scan
            navigate('/receipt', {
              state: {
                ticket: ticketResponse.data.data,
                transactionId,
                paymentFlow: 'nfc', // Indicates kiosk should show ticket QR
              },
              replace: true,
            });
          } catch (ticketError) {
            console.error('[NFCPayment] Failed to fetch ticket:', ticketError);
            setStatus('failed');
            setMessage('Payment completed but failed to load ticket. Please contact staff.');
          }
        } else if (result.status === 'CANCELLED' || result.status === 'cancelled') {
          setStatus('failed');
          setMessage('Payment was cancelled.');
          clearTimers();
        } else {
          setStatus('failed');
          setMessage(result.error || 'Payment failed. Please try again.');
          clearTimers();
        }
      } catch (error: any) {
        console.error('[NFCPayment] Payment error:', error);
        setStatus('failed');
        setMessage(error.message || 'Failed to process payment. Please try again.');
        clearTimers();
      }
    };

    // Check for pending result from before app reload
    const pendingResult = androidBridge.checkPendingPaymentResult();
    if (pendingResult && pendingResult.transactionId?.includes(transactionId)) {
      console.log('[NFCPayment] Found pending result:', pendingResult);
      if (pendingResult.status === 'success' || pendingResult.status === 'APPROVED' || pendingResult.status === 'COMPLETED') {
        setStatus('completed');
        setMessage('Payment successful! Loading your ticket...');

        // Process pending result
        (async () => {
          try {
            await api.post(`/payment/viva/pos-payment-complete`, {
              transaction_id: transactionId,
              status: 'success',
              viva_transaction_id: pendingResult.transactionId,
              session_id: pendingResult.sessionId,
              payment_method: 'CARD_PRESENT',
            });

            const ticketResponse = await api.get(`/transaction/${transactionId}/ticket`);
            navigate('/receipt', {
              state: {
                ticket: ticketResponse.data.data,
                transactionId,
                paymentFlow: 'nfc',
              },
              replace: true,
            });
          } catch (error) {
            console.error('[NFCPayment] Error processing pending result:', error);
            setStatus('failed');
            setMessage('Payment completed but failed to load ticket.');
          }
        })();
        return;
      }
    }

    initializePayment();

    return () => {
      clearTimers();
      paymentInitiatedRef.current = false;
    };
  }, [transactionId, amount, sessionToken, clearTimers, navigate, retryCount]);

  const handleCancel = () => {
    clearTimers();
    navigate('/');
  };

  const handleRetry = () => {
    paymentInitiatedRef.current = false;
    setStatus('checking');
    setMessage('Checking device capabilities...');
    setElapsedTime(0);
    setRetryCount((c) => c + 1);
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-800 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white/95 backdrop-blur-md rounded-3xl shadow-2xl p-8 border border-white/20">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {(status === 'checking' || status === 'launching') && (
              <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent"></div>
              </div>
            )}

            {status === 'waiting' && (
              <div className="w-24 h-24 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.14 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
                </svg>
              </div>
            )}

            {status === 'completed' && (
              <div className="w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}

            {(status === 'failed' || status === 'unsupported') && (
              <div className="w-24 h-24 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center shadow-lg">
                <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
            {status === 'checking' && 'Checking Device'}
            {status === 'launching' && 'Launching Payment App'}
            {status === 'waiting' && 'Tap to Pay'}
            {status === 'completed' && 'Payment Successful!'}
            {status === 'failed' && 'Payment Failed'}
            {status === 'unsupported' && 'Not Available'}
          </h2>

          {/* Message */}
          <p className="text-gray-600 text-center mb-6">{message}</p>

          {/* Amount */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-gray-600">Amount</span>
              <span className="text-2xl font-bold text-gray-900">&euro;{parseFloat(String(amount)).toFixed(2)}</span>
            </div>
            {ticketType?.name && (
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Ticket</span>
                <span className="text-sm font-medium text-gray-700">{ticketType.name}</span>
              </div>
            )}
          </div>

          {/* NFC Animation for waiting state */}
          {status === 'waiting' && (
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-purple-600 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-purple-900">Ready for payment</p>
                  <p className="text-xs text-purple-700">Tap your card or phone on the device</p>
                </div>
              </div>
            </div>
          )}

          {/* Elapsed time */}
          {status === 'waiting' && (
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500">Waiting time: {formatTime(elapsedTime)}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col space-y-3">
            {status === 'failed' && (
              <button
                onClick={handleRetry}
                className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg"
              >
                Try Again
              </button>
            )}

            {(status === 'checking' || status === 'launching' || status === 'waiting' || status === 'failed' || status === 'unsupported') && (
              <button
                onClick={handleCancel}
                className="w-full bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
            )}
          </div>

          {/* Footer */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-2 text-gray-500">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              <span className="text-xs font-medium">NFC Payment</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NFCPaymentFlow;
